import React, { useState, useEffect } from 'react'

/**
 * SnowflakeIcon Component
 * 
 * Renders different states of the snowflake icon to indicate freeze status:
 * - frozen: filled snowflake (white on blue background)
 * - unfrozen: outline snowflake (gray)
 * - partially_frozen: half-filled snowflake (blue on white background)
 * 
 * This is a controlled component that syncs its internal state with external props,
 * similar to how Checkbox works. Each instance maintains its own state identity.
 * 
 * @param {string} state - "frozen", "unfrozen", or "partially_frozen" (external state)
 * @param {string} className - Additional CSS classes
 * @param {function} onClick - Click handler
 * @param {string} title - Tooltip text
 */
export default function SnowflakeIcon({ state = "unfrozen", className = "", onClick, title }) {
  // Internal state management - makes this a controlled component like Checkbox
  const [internalState, setInternalState] = useState(state)
  const [hasLoggedMount, setHasLoggedMount] = useState(false)
  
  // Log component mount only once
  useEffect(() => {
    setHasLoggedMount(true)
  }, [])
  
  // Sync internal state with external prop changes (like Checkbox useEffect pattern)
  useEffect(() => {
    setInternalState(state)
  }, [state])
  
  const getBackgroundClasses = () => {
    switch (internalState) {
      case "frozen": 
        return "bg-admin_accent_2 text-white border border-white rounded-full p-1"
      case "partially_frozen": 
        return "bg-white text-admin_accent_2 border border-admin_accent_2 rounded-full p-1"
      case "unfrozen":
      default: 
        return "bg-white text-admin_neutral_3/70 hover:text-admin_neutral_3 border border-admin_neutral_2 rounded-full p-1"
    }
  }
  
  const baseClasses = "cursor-pointer transition-all duration-200 transform hover:scale-105"
  const backgroundClasses = getBackgroundClasses()
  const combinedClasses = `${baseClasses} ${backgroundClasses} ${className}`

  const getFillColor = () => {
    switch (internalState) {
      case "frozen": return "currentColor"
      case "partially_frozen": return "url(#half-frozen-gradient)"
      case "unfrozen":
      default: return "currentColor"
    }
  }

  const getStrokeColor = () => {
    return "currentColor"
  }

  const handleClick = (e) => {
    const newState = internalState === "frozen" ? "unfrozen" : "frozen"
    
    // Handle internal state toggle for immediate visual feedback (like Checkbox)
    setInternalState(newState)
    
    if (onClick) {
      onClick(e)
    }
  }

  return (
    <div className={combinedClasses} onClick={handleClick} title={title}>
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="flex-shrink-0"
      >
      <defs>
        <linearGradient id="half-frozen-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="50%" stopColor="currentColor" />
          <stop offset="50%" stopColor="rgba(156, 163, 175, 0.5)" />
        </linearGradient>
      </defs>
      
      {/* Snowflake paths */}
      <g stroke={getStrokeColor()} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        {/* Main cross */}
        <line x1="12" y1="2" x2="12" y2="22" fill={getFillColor()} />
        <line x1="22" y1="12" x2="2" y2="12" fill={getFillColor()} />
        
        {/* Diagonal lines */}
        <line x1="20" y1="4" x2="4" y2="20" fill={getFillColor()} />
        <line x1="4" y1="4" x2="20" y2="20" fill={getFillColor()} />
        
        {/* Top branches */}
        <line x1="12" y1="2" x2="9" y2="5" fill={getFillColor()} />
        <line x1="12" y1="2" x2="15" y2="5" fill={getFillColor()} />
        
        {/* Bottom branches */}
        <line x1="12" y1="22" x2="9" y2="19" fill={getFillColor()} />
        <line x1="12" y1="22" x2="15" y2="19" fill={getFillColor()} />
        
        {/* Left branches */}
        <line x1="2" y1="12" x2="5" y2="9" fill={getFillColor()} />
        <line x1="2" y1="12" x2="5" y2="15" fill={getFillColor()} />
        
        {/* Right branches */}
        <line x1="22" y1="12" x2="19" y2="9" fill={getFillColor()} />
        <line x1="22" y1="12" x2="19" y2="15" fill={getFillColor()} />
      </g>
      
        {/* Center dot for frozen state - use internalState */}
        {(internalState === "frozen" || internalState === "partially_frozen") && (
          <circle 
            cx="12" 
            cy="12" 
            r="1.5" 
            fill={getFillColor()} 
            stroke="none"
          />
        )}
      </svg>
    </div>
  )
}

/**
 * Helper function to determine freeze state from translation data
 * @param {Object} translation - Translation object with frozen array
 * @param {string|Array} fieldKeyOrFrozenArray - Optional field key for object translations, or frozen array override
 * @returns {string} - "frozen", "unfrozen", or "partially_frozen"
 */
// Performance optimization: cache recent freeze state calculations
const freezeStateCache = new Map()
const CACHE_MAX_SIZE = 100

export const getFreezeState = (translation, fieldKeyOrFrozenArray = null) => {
  // Create cache key for memoization
  const cacheKey = JSON.stringify({ 
    frozen: translation?.frozen, 
    fieldKey: fieldKeyOrFrozenArray 
  })
  
  if (freezeStateCache.has(cacheKey)) {
    return freezeStateCache.get(cacheKey)
  }
  
  // Handle case where second parameter is frozen array override (from local state)
  let frozenArray
  let fieldKey = null
  
  if (Array.isArray(fieldKeyOrFrozenArray)) {
    frozenArray = fieldKeyOrFrozenArray
  } else {
    fieldKey = fieldKeyOrFrozenArray
    
    if (!translation || !translation.frozen || !Array.isArray(translation.frozen)) {
      const result = "unfrozen"
      // Cache and return
      if (freezeStateCache.size >= CACHE_MAX_SIZE) {
        const firstKey = freezeStateCache.keys().next().value
        freezeStateCache.delete(firstKey)
      }
      freezeStateCache.set(cacheKey, result)
      return result
    }
    frozenArray = translation.frozen
  }
  
  let result
  
  // If frozen array is empty, everything is unfrozen
  if (frozenArray.length === 0) {
    result = "unfrozen"
  }
  // If frozen array contains "*", everything is fully frozen
  else if (frozenArray.includes("*")) {
    result = "frozen"
  }
  else if (fieldKey) {
    // For object translations, check if specific field is in frozen array
    const isFieldFrozen = frozenArray.includes(fieldKey)
    result = isFieldFrozen ? "frozen" : "unfrozen"
  }
  else {
    // For regular translations, if we have specific field names in frozen array, it's partially frozen
    result = "partially_frozen"
  }
  
  // Cache the result
  if (freezeStateCache.size >= CACHE_MAX_SIZE) {
    const firstKey = freezeStateCache.keys().next().value
    freezeStateCache.delete(firstKey)
  }
  freezeStateCache.set(cacheKey, result)
  
  return result
}

/**
 * Helper function to get appropriate title text for freeze state
 * @param {string} state - "frozen", "unfrozen", or "partially_frozen"
 * @param {string} fieldKey - Optional field key for object translations
 * @returns {string} - Title text for tooltip
 */
export const getFreezeTitle = (state, fieldKey = null) => {
  const fieldText = fieldKey ? ` (${fieldKey})` : ""
  
  switch (state) {
    case "frozen": 
      return `This translation will not be affected by auto-translation${fieldText}`
    case "partially_frozen": 
      return "Some fields will not be affected by auto-translation"
    case "unfrozen":
    default: 
      return `Allow auto-translation${fieldText}`
  }
}